import { useQueries, useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../auth/useAuth';
import { lacAnalyticsService } from '../../../services/LacAnalyticsService';
import type { LacTenantOverviewDateRange } from '../../../types/lac';
import { lacQueryKeys } from '../lacQueryKeys';

export function useLacTenantOverviewsQuery(
  range?: LacTenantOverviewDateRange,
  options?: { enabled?: boolean },
) {
  const { user } = useAuth();
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: lacQueryKeys.tenantOverviews(range ?? null),
    enabled: Boolean(user) && enabled,
    queryFn: () => lacAnalyticsService.listTenantOverviews(range),
  });
}

/** Parallel overview fetches that share `lacQueryKeys.tenantOverviews` cache entries. */
export function useLacTenantOverviewsQueries(
  ranges: LacTenantOverviewDateRange[],
  options?: { enabled?: boolean },
) {
  const { user } = useAuth();
  const enabled = options?.enabled ?? true;

  return useQueries({
    queries: ranges.map((range) => ({
      queryKey: lacQueryKeys.tenantOverviews(range),
      queryFn: () => lacAnalyticsService.listTenantOverviews(range),
      enabled: Boolean(user) && enabled,
    })),
  });
}

export function useLacTenantOverviewQuery(
  tenantName: string | undefined,
  range?: LacTenantOverviewDateRange,
) {
  return useQuery({
    queryKey: lacQueryKeys.tenantOverview(tenantName ?? '', range ?? null),
    enabled: Boolean(tenantName),
    queryFn: () => {
      if (!tenantName) {
        throw new Error('Missing tenant name');
      }
      return lacAnalyticsService.getTenantOverview(tenantName, range);
    },
  });
}
